/**
 * Generates a Table of Contents from markdown content
 * Extracts headings (H2-H4) and creates a hierarchical structure
 */

/**
 * Generate a slug/anchor from heading text
 * @param {string} text - The heading text
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = text => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .trim()
}

/**
 * Extract headings from markdown content
 * @param {string} content - Raw markdown content
 * @returns {Array} - Array of heading objects { level, text, slug }
 */
export const extractHeadings = content => {
  if (!content) return []

  const lines = content.split('\n')
  const headings = []

  lines.forEach(line => {
    // Match markdown headings (## Heading, ### Heading, etc.)
    const match = line.match(/^(#{2,4})\s+(.+)$/)
    if (match) {
      const level = match[1].length // Count # symbols
      const text = match[2].trim()
      const slug = generateSlug(text)

      headings.push({
        level,
        text,
        slug,
      })
    }
  })

  return headings
}

/**
 * Build hierarchical TOC structure
 * @param {Array} headings - Flat array of headings
 * @returns {Array} - Hierarchical TOC structure
 */
export const buildTOCTree = headings => {
  if (!headings || headings.length === 0) return []

  const tree = []
  const stack = []

  headings.forEach(heading => {
    const node = {
      ...heading,
      children: [],
    }

    // Pop stack until we find a parent with lower level
    while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
      stack.pop()
    }

    if (stack.length === 0) {
      // This is a top-level heading
      tree.push(node)
    } else {
      // Add as child of the last item in stack
      stack[stack.length - 1].children.push(node)
    }

    stack.push(node)
  })

  return tree
}

/**
 * Generate complete TOC from markdown content
 * @param {string} content - Raw markdown content
 * @returns {Array} - Hierarchical TOC structure
 */
export const generateTOC = content => {
  const headings = extractHeadings(content)
  return buildTOCTree(headings)
}
